import { Folder, Search } from "lucide-react";
import { Input } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import { ProjectDetails } from "../../types";
import { useSetRecoilState, useRecoilValue } from "recoil";
import { projectsState, currentProjectState } from "../../recoil/project";
import { useProjects } from "../../hooks/queries/projects";
import { environmentKeys } from "../../hooks/queries/environments";
import { getEnvironmentService } from "../../backend";
import ProjectCard from "../../components/cards/ProjectCard";
import CardGridSkeleton from "../../components/cards/CardGridSkeleton";
import EmptyState from "../../components/EmptyState";

export default function Projects() {
    const navigate = useNavigate();
    const location = useLocation();
    const setProjects = useSetRecoilState(projectsState);
    const currentProject = useRecoilValue(currentProjectState);
    const [searchText, setSearchText] = useState("");

    // Use TanStack Query for projects
    const { data: projectsData = [], isLoading: loadingProjects } = useProjects();

    // Fetch environments for each project using TanStack Query with separate cache keys
    // Using a different query key (listForProjects) to avoid conflicts with environments list page
    const environmentQueries = useQueries({
        queries: projectsData.map((project) => ({
            queryKey: environmentKeys.listForProjects(project.id),
            queryFn: async () => {
                const { data: { stages } } = await getEnvironmentService().getStages(project.id);
                return { projectId: project.id, environments: stages };
            },
            staleTime: 30 * 1000,
            gcTime: 5 * 60 * 1000,
            enabled: !!project.id,
        })),
    });

    // Combine projects with their environments from TanStack Query cache
    const projects = useMemo(() => {
        return projectsData.map((project) => {
            const envQuery = environmentQueries.find(
                (q) => q.data?.projectId === project.id
            );
            return {
                ...project,
                environments: envQuery?.data?.environments,
            };
        });
    }, [projectsData, environmentQueries]);

    // Update Recoil state with projects (without environments) for other parts of the app
    // This ensures environments fetched here don't interfere with the environments list page
    useEffect(() => {
        if (projectsData.length > 0) {
            // Store projects without environments in Recoil to avoid mixing with environments list page
            setProjects(projectsData);
        }
    }, [projectsData, setProjects]);

    // Handle navigation logic
    useEffect(() => {
        if (projects.length > 0 && !loadingProjects) {
            const showProjectsList = (location.state as any)?.showProjectsList === true;
            if (currentProject?.id && !showProjectsList) {
                const projectExists = projects.find(p => p.id === currentProject.id);
                if (projectExists) {
                    navigate(`/projects/${currentProject.id}/environments`, { replace: true });
                }
            }
        }
    }, [projects, loadingProjects, currentProject, location.state, navigate]);

    // Filter projects using useMemo for performance
    const filteredProjects = useMemo(() => {
        if (searchText) {
            return projects.filter(project =>
                project.name.toLowerCase().includes(searchText.toLowerCase()) ||
                project.description?.toLowerCase().includes(searchText.toLowerCase()) ||
                project.environments?.some(e => e.name.toLowerCase().includes(searchText.toLowerCase()))
            );
        }
        return projects;
    }, [projects, searchText]);

    const loading = loadingProjects;

    if (loading) {
        return <CardGridSkeleton variant="project" />;
    }

    return (
        <>
            {projects.length > 0 && (
                <>
                    {projects.length > 6 && (
                        <div className="card-search">
                            <Input
                                placeholder="Search projects..."
                                prefix={<Search size={16} style={{ color: '#9CA3AF' }} />}
                                allowClear
                                size="large"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                        </div>
                    )}
                    <div className="cards-grid">
                        {filteredProjects.map((project) => (
                            <ProjectCard key={project.id} project={project} />
                        ))}
                    </div>
                    {filteredProjects.length === 0 && searchText && (
                        <EmptyState
                            icon={<Search size={48} />}
                            title="No projects found"
                            description={`No projects match your search "${searchText}". Try a different search term.`}
                        />
                    )}
                </>
            )}

            {!loading && projects.length === 0 && (
                <EmptyState
                    icon={<Folder size={48} />}
                    title="Welcome to Microtica!"
                    description="Projects help you organize your infrastructure and applications. Create your first project to get started."
                    actionText="Create Project"
                    actionLink="/projects/create"
                />
            )}
        </>
    );
}
